import { useEffect, useState } from "react";
import { CaretLeft, CaretRight, Eye, FilePdf, FloppyDisk, Trash, X, EnvelopeSimple } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { wibNow } from "@/lib/time";
import { PageHeader } from "@/components/PageHeader";
import { KwitansiView } from "@/components/KwitansiView";
import { useAuth } from "@/context/AuthContext";
import { KW_EMAIL, MON_FULL, exportKwitansiPdf, fmtDate } from "@/lib/kwitansi";
import { toast } from "sonner";

const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => { const d = wibNow(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const emptyForm = () => {
  const d = wibNow();
  return { received_from: "", student_name: "", student_class: "", amount: 0, date: todayStr(), pay_dev: false, uniform: false, spp: false,
    spp_month: d.getMonth() + 1, spp_year: d.getFullYear(), other: false, other_text: "" };
};
const inputCls = "w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mt-1 outline-none focus:border-primary text-sm";

const CheckRow = ({ checked, onChange, label, testid, children }) => (
  <label className="flex items-center gap-3 bg-white border border-stone-200/70 rounded-2xl px-4 py-3 cursor-pointer">
    <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 accent-[hsl(var(--primary))]" />
    <span className="text-sm text-stone-700 flex-1">{label}</span>
    {children}
  </label>
);

const cellCls = "bg-stone-50 border border-stone-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-primary";
const updateKw = async (k, patch, onSaved) => {
  try { await api.put(`/admin/kwitansi/${k.id}`, patch); toast.success("Tersimpan"); }
  catch (e) { toast.error(apiError(e.response?.data?.detail) || "Gagal menyimpan"); }
  onSaved();
};

const EditCell = ({ k, field, type = "text", max, width = "w-32", onSaved, prefix }) => {
  const raw = k[field] ?? "";
  const show = (x) => (type === "money" ? (x ? Number(x).toLocaleString("id-ID") : "") : x);
  const [v, setV] = useState(show(raw));
  useEffect(() => { setV(show(raw)); /* eslint-disable-next-line */ }, [raw]);
  const save = () => {
    const val = type === "money" ? Number(String(v).replace(/\D/g, "")) || 0 : v;
    if (val === raw) return;
    if ((field === "received_from" && !String(val).trim()) || (type === "money" && val <= 0) || (type === "date" && !val)) { setV(show(raw)); toast.error("Isian tidak boleh kosong"); return; }
    updateKw(k, { [field]: val }, onSaved);
  };
  const input = (
    <input data-testid={`kwitansi-cell-${field}-${k.seq}`} type={type === "date" ? "date" : "text"} inputMode={type === "money" ? "numeric" : undefined}
      value={v} maxLength={max} onChange={(e) => setV(type === "money" ? show(Number(e.target.value.replace(/\D/g, "")) || "") : e.target.value)}
      onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} className={`${cellCls} ${width} ${prefix ? "flex-1 min-w-0" : ""}`} />
  );
  return prefix ? <div className={`flex items-center gap-1 ${width}`}><span className="text-stone-500">{prefix}</span>{input}</div> : input;
};

const PayCell = ({ k, onSaved }) => {
  const [other, setOther] = useState(k.other_text || "");
  useEffect(() => { setOther(k.other_text || ""); }, [k.other_text]);
  const tog = (f) => updateKw(k, { [f]: !k[f] }, onSaved);
  const Box = ({ f, label }) => (
    <label className="flex items-center gap-1.5 whitespace-nowrap cursor-pointer">
      <input type="checkbox" data-testid={`kwitansi-cell-${f}-${k.seq}`} checked={!!k[f]} onChange={() => tog(f)} className="w-3.5 h-3.5 accent-[hsl(var(--primary))]" />{label}
    </label>
  );
  return (
    <div className="space-y-1 w-56">
      <Box f="pay_dev" label="Pembangunan & Pengembangan Pend." />
      <Box f="uniform" label="Seragam" />
      <div className="flex items-center gap-1">
        <Box f="spp" label="SPP" />
        {k.spp && (<>
          <select data-testid={`kwitansi-cell-spp_month-${k.seq}`} value={k.spp_month || 1} onChange={(e) => updateKw(k, { spp_month: Number(e.target.value) }, onSaved)} className={cellCls}>
            {MON_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input data-testid={`kwitansi-cell-spp_year-${k.seq}`} type="number" defaultValue={k.spp_year || ""} onBlur={(e) => { const y = Number(e.target.value); if (y >= 2000 && y <= 2100 && y !== k.spp_year) updateKw(k, { spp_year: y }, onSaved); }} className={`${cellCls} w-16`} />
        </>)}
      </div>
      <div className="flex items-center gap-1">
        <Box f="other" label="Lain" />
        {k.other && <input data-testid={`kwitansi-cell-other_text-${k.seq}`} value={other} maxLength={40} onChange={(e) => setOther(e.target.value)}
          onBlur={() => { if (other !== (k.other_text || "")) updateKw(k, { other_text: other }, onSaved); }} className={`${cellCls} flex-1 min-w-0`} />}
      </div>
    </div>
  );
};

const TH = ["No.", "Nama Murid", "Kelas", "Nama Pembayar", "Tgl Bayar", "Jumlah dibayar", "Keterangan bayar", "No Kwitansi", "Keterangan", ""];
const KwitansiTable = ({ list, onDelete, onReload, onView }) => (
  <div className="overflow-x-auto rounded-2xl border border-stone-200/60 bg-white shadow-sm" data-testid="kwitansi-table">
    <table className="w-full text-xs">
      <thead><tr className="bg-primary text-white">{TH.map((h) => <th key={h} className="py-2 px-2 text-left font-semibold whitespace-nowrap align-top">{h}</th>)}</tr></thead>
      <tbody>
        {list.length === 0 && <tr><td colSpan={TH.length} className="py-4 text-center text-stone-400">Belum ada data.</td></tr>}
        {list.map((k, i) => (
          <tr key={k.id} data-testid={`kwitansi-row-${k.seq}`} className="border-t border-stone-100 align-top">
            <td className="py-2 px-2 text-stone-500">{i + 1}</td>
            <td className="py-2 px-2"><EditCell k={k} field="student_name" max={60} width="w-40" prefix="Ananda" onSaved={onReload} /></td>
            <td className="py-2 px-2"><EditCell k={k} field="student_class" max={30} width="w-20" onSaved={onReload} /></td>
            <td className="py-2 px-2"><EditCell k={k} field="received_from" max={80} width="w-40" onSaved={onReload} /></td>
            <td className="py-2 px-2"><EditCell k={k} field="date" type="date" width="w-32" onSaved={onReload} /></td>
            <td className="py-2 px-2"><EditCell k={k} field="amount" type="money" width="w-28" prefix="Rp" onSaved={onReload} /></td>
            <td className="py-2 px-2"><PayCell k={k} onSaved={onReload} /></td>
            <td className="py-2 px-2 whitespace-nowrap text-primary font-semibold">{k.number}</td>
            <td className="py-2 px-2"><EditCell k={k} field="note" max={300} width="w-40" onSaved={onReload} /></td>
            <td className="py-2 px-2">
              <div className="flex gap-1">
                <button data-testid={`kwitansi-row-view-${k.seq}`} onClick={() => onView(k)} className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Eye size={14} weight="bold" /></button>
                <button data-testid={`kwitansi-row-delete-${k.seq}`} onClick={() => onDelete(k)} className="w-8 h-8 rounded-full bg-red-50 text-red-500 flex items-center justify-center"><Trash size={14} weight="bold" /></button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PreviewModal = ({ data, school, onClose }) => (
  <div className="fixed inset-0 z-50 bg-stone-900 flex flex-col" data-testid="kwitansi-preview-modal">
    <div className="flex items-center justify-between p-3 text-white">
      <p className="font-heading font-semibold text-sm">Preview Kwitansi {data.number}</p>
      <button data-testid="kwitansi-preview-close" onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><X size={18} /></button>
    </div>
    <div className="flex-1 flex items-center justify-center p-3 overflow-auto">
      <div className="w-full max-w-5xl"><KwitansiView data={data} school={school} testid="kwitansi-preview" /></div>
    </div>
    <div className="p-4 grid grid-cols-2 gap-2 max-w-xl w-full mx-auto">
      <button data-testid="kwitansi-preview-pdf" onClick={() => exportKwitansiPdf(data, school).catch(() => toast.error("Gagal membuat PDF"))}
        className="bg-white text-stone-800 font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
        <FilePdf size={18} weight="bold" /> Export PDF
      </button>
      <a data-testid="kwitansi-preview-email" href={`mailto:${KW_EMAIL}?subject=${encodeURIComponent(`Kwitansi ${data.number || ""}`)}&body=${encodeURIComponent(`Kwitansi ${data.number || ""} - ${data.received_from} (${fmtDate(data.date)}). File PDF terlampir.`)}`}
        className="bg-primary text-white font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
        <EnvelopeSimple size={18} weight="bold" /> Kirim Email
      </a>
      <p className="col-span-2 text-white/60 text-xs text-center">Unduh PDF dulu, lalu lampirkan saat email ke {KW_EMAIL} terbuka.</p>
    </div>
  </div>
);

export default function AdminKwitansi() {
  const { user } = useAuth();
  const school = user?.school === "sdqu" ? "sdqu" : "tk";
  const [form, setForm] = useState(emptyForm);
  const [nextNo, setNextNo] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const now = wibNow();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [list, setList] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadList = () => api.get(`/admin/kwitansi?month=${month}&year=${year}`).then((r) => setList(r.data)).catch(() => toast.error("Gagal memuat arsip"));
  const loadNext = () => { if (form.date) api.get(`/admin/kwitansi/next-number?date=${form.date}`).then((r) => setNextNo(r.data.number)).catch(() => setNextNo("")); };
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [month, year]);
  useEffect(() => { loadNext(); /* eslint-disable-next-line */ }, [form.date]);

  const draft = { ...form, number: nextNo };
  const valid = form.received_from.trim() && form.amount > 0 && form.date;

  const save = async (andPdf) => {
    if (!valid) { toast.error("Isi nama dan jumlah uang terlebih dahulu"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/kwitansi", form);
      toast.success(`Kwitansi ${data.number} tersimpan`);
      if (andPdf) await exportKwitansiPdf(data, school);
      setForm(emptyForm());
      const d = new Date(data.date);
      if (d.getMonth() + 1 === month && d.getFullYear() === year) loadList(); else { setMonth(d.getMonth() + 1); setYear(d.getFullYear()); }
      loadNext();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Gagal menyimpan"); } finally { setSaving(false); }
  };

  const remove = async (k) => {
    if (!window.confirm(`Hapus kwitansi ${k.number} (${k.received_from})?`)) return;
    try { await api.delete(`/admin/kwitansi/${k.id}`); toast.success("Kwitansi dihapus"); loadList(); loadNext(); }
    catch (e) { toast.error("Gagal menghapus"); }
  };

  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  return (
    <div>
      <PageHeader title="Kwitansi" />
      <div className="px-5 pt-6 pb-10 space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-heading font-semibold text-stone-800 text-sm">Isi Kwitansi</p>
          <span data-testid="kwitansi-next-number" className="text-xs font-semibold bg-primary/10 text-primary px-3 py-1 rounded-full">No: {nextNo || "-"}</span>
        </div>
        <div>
          <label className="text-xs text-stone-500">Sudah Terima Dari</label>
          <input data-testid="kwitansi-from-input" maxLength={80} value={form.received_from} onChange={(e) => set("received_from", e.target.value)} placeholder="Nama pembayar" className={inputCls} />
        </div>
        <div className="grid grid-cols-5 gap-2">
          <div className="col-span-3">
            <label className="text-xs text-stone-500">Nama Murid</label>
            <div className="flex items-center bg-white border border-stone-200 rounded-2xl mt-1 focus-within:border-primary overflow-hidden">
              <span className="pl-4 pr-1 text-sm text-stone-500 font-semibold">Ananda</span>
              <input data-testid="kwitansi-student-input" maxLength={60} value={form.student_name} onChange={(e) => set("student_name", e.target.value)} placeholder="nama murid" className="flex-1 min-w-0 py-3 pr-4 outline-none text-sm bg-transparent" />
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-stone-500">Kelas</label>
            <input data-testid="kwitansi-class-input" maxLength={30} value={form.student_class} onChange={(e) => set("student_class", e.target.value)} placeholder="mis. TK B" className={inputCls} />
          </div>
        </div>
        <div>
          <label className="text-xs text-stone-500">Banyaknya Uang (Rp.)</label>
          <input data-testid="kwitansi-amount-input" inputMode="numeric" value={form.amount ? form.amount.toLocaleString("id-ID") : ""}
            onChange={(e) => set("amount", Number(e.target.value.replace(/\D/g, "")) || 0)} placeholder="0" className={inputCls} />
        </div>
        <div className="space-y-2">
          <p className="text-xs text-stone-500">Untuk Pembayaran</p>
          <CheckRow testid="kwitansi-dev-check" checked={form.pay_dev} onChange={(v) => set("pay_dev", v)} label="Pembangunan & Pengembangan Pendidikan" />
          <CheckRow testid="kwitansi-uniform-check" checked={form.uniform} onChange={(v) => set("uniform", v)} label="Seragam" />
          <CheckRow testid="kwitansi-spp-check" checked={form.spp} onChange={(v) => set("spp", v)} label="SPP" />
          {form.spp && (
            <div className="grid grid-cols-2 gap-2 pl-2">
              <select data-testid="kwitansi-spp-month" value={form.spp_month} onChange={(e) => set("spp_month", Number(e.target.value))} className={inputCls}>
                {MON_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select data-testid="kwitansi-spp-year" value={form.spp_year} onChange={(e) => set("spp_year", Number(e.target.value))} className={inputCls}>
                {[0, 1, 2, 3].map((o) => now.getFullYear() - 1 + o).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
          <CheckRow testid="kwitansi-other-check" checked={form.other} onChange={(v) => set("other", v)} label="Lainnya" />
          {form.other && <input data-testid="kwitansi-other-input" maxLength={40} value={form.other_text} onChange={(e) => set("other_text", e.target.value)} placeholder="Tulis keterangan pembayaran lain" className={inputCls} />}
        </div>
        <div>
          <label className="text-xs text-stone-500">Tanggal</label>
          <input type="date" data-testid="kwitansi-date-input" value={form.date} onChange={(e) => set("date", e.target.value || todayStr())} className={inputCls} />
        </div>

        <p className="font-heading font-semibold text-stone-800 text-sm pt-2">Tampilan Kwitansi</p>
        <KwitansiView data={draft} school={school} testid="kwitansi-live" />
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="kwitansi-preview-btn" onClick={() => setPreview(draft)}
            className="border-2 border-primary text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <Eye size={18} weight="bold" /> Preview
          </button>
          <button data-testid="kwitansi-save-btn" onClick={() => save(false)} disabled={saving}
            className="border-2 border-primary text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50">
            <FloppyDisk size={18} weight="bold" /> Simpan
          </button>
          <button data-testid="kwitansi-save-pdf-btn" onClick={() => save(true)} disabled={saving}
            className="col-span-2 bg-primary text-white font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50">
            <FilePdf size={18} weight="bold" /> {saving ? "Menyimpan..." : "Simpan & Export PDF"}
          </button>
        </div>

        <div className="border-t border-stone-200/70 pt-5">
          <div className="flex items-center justify-between mb-3">
            <p className="font-heading font-semibold text-stone-800 text-sm">Tabel Kwitansi</p>
            <div className="flex items-center gap-1">
              <button data-testid="kwitansi-prev-month" onClick={prev} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center"><CaretLeft size={14} weight="bold" /></button>
              <span data-testid="kwitansi-month-label" className="text-sm font-semibold text-stone-700 w-32 text-center">{MON_FULL[month - 1]} {year}</span>
              <button data-testid="kwitansi-next-month" onClick={next} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center"><CaretRight size={14} weight="bold" /></button>
            </div>
          </div>
          <KwitansiTable list={list} onDelete={remove} onReload={() => { loadList(); loadNext(); }} onView={setPreview} />
        </div>
      </div>
      {preview && <PreviewModal data={preview} school={school} onClose={() => setPreview(null)} />}
    </div>
  );
}
